import React, { FC, useEffect, useState } from 'react';
import { RiDeleteBin5Line } from 'react-icons/ri';
import {
  LuTriangle,
  LuInfo,
  LuRefreshCw,
  LuAlertTriangle,
  LuCheckCircle,
} from 'react-icons/lu';
import {
  Modal,
  type ModalVariant,
  type ModalSize,
} from '@/components/elements/Modal';
import ConfigInput from '@/components/elements/ConfigInput';
import { Text } from '@/components/atoms/AppText';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { APP_BG, APP_HEX } from '@/constant/theme/colors.constant';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ConfirmActionModalProps {
  /** Controls visibility */
  isOpen: boolean;
  /** Called when the modal is closed (X, backdrop, Cancel) */
  onClose: () => void;
  /** Called when the confirm button is clicked and the typed word matches */
  onConfirm: () => void;

  // ── Content ──────────────────────────────────────────────────────────────
  /** Modal header title */
  title: string;
  /** Modal header subtitle (e.g. "This action cannot be undone.") */
  subtitle?: string;
  /** Main descriptive paragraph. Use `{name}` slot or pass a ReactNode. */
  description: React.ReactNode;
  /** Text the user must type to enable the confirm button (e.g. "delete").
   *  When omitted the confirm button is always enabled — no input is shown. */
  confirmWord?: string;
  /** Placeholder shown inside the input (e.g. 'please type `delete`') */
  placeholder?: string;
  /** Label shown above the input */
  inputLabel?: string;
  /** Hint text shown below the input */
  hint?: string;

  // ── Button labels ─────────────────────────────────────────────────────────
  /** Confirm button label (default: "Confirm") */
  confirmLabel?: React.ReactNode;
  /** Icon rendered inside the confirm button */
  confirmIcon?: React.ReactNode;
  /** Cancel button label (default: "Cancel") */
  cancelLabel?: string;

  // ── Appearance ────────────────────────────────────────────────────────────
  /** Modal variant — controls colour theme (default: "danger") */
  variant?: ModalVariant;
  /** Modal size (default: "md") */
  size?: ModalSize;
  /** Icon rendered in the modal header circle */
  icon?: React.ReactNode;

  // ── State ─────────────────────────────────────────────────────────────────
  /** Show spinner on the confirm button and block interaction */
  loading?: boolean;
}

// ─── Named icon exports ──────────────────────────────────────────────────────
export const DeleteIcon = () => <RiDeleteBin5Line className="w-4 h-4" />;
export const ActivateIcon = () => <LuRefreshCw className="w-4 h-4" />;

const defaultVariantIcon: Record<ModalVariant, React.ReactNode> = {
  default: <LuInfo className="w-4 h-4" />,
  danger: <LuTriangle className="w-4 h-4" />,
  warning: <LuAlertTriangle className="w-4 h-4" />,
  info: <LuInfo className="w-4 h-4" />,
  success: <LuCheckCircle className="w-4 h-4" />,
};

const inputFocusColors: Record<ModalVariant, string> = {
  default: 'rgb(var(--color-primary-rgb))',
  danger: APP_HEX.redSoft,
  warning: APP_HEX.amberSoft,
  info: APP_HEX.blueSoft,
  success: APP_HEX.emeraldSoft,
};

// ─── Component ────────────────────────────────────────────────────────────────

const ConfirmActionModal: FC<ConfirmActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  subtitle,
  description,
  confirmWord,
  placeholder,
  inputLabel = ORGANISM_PROPS.CONFIRM_ACTION_MODAL.INPUT_LABEL,
  hint,
  confirmLabel = ORGANISM_PROPS.CONFIRM_ACTION_MODAL.CONFIRM_LABEL,
  confirmIcon,
  cancelLabel = ORGANISM_PROPS.CONFIRM_ACTION_MODAL.CANCEL_LABEL,
  variant = 'danger',
  size = 'md',
  icon,
  loading = false,
}) => {
  const [typed, setTyped] = useState('');
  const requiresInput = Boolean(confirmWord);
  const isConfirmEnabled = requiresInput
    ? typed.trim() === confirmWord && !loading
    : !loading;
  const resolvedIcon = icon ?? defaultVariantIcon[variant];
  const resolvedSize = size ?? (requiresInput ? 'md' : 'sm');

  // Clear input whenever modal opens/closes
  useEffect(() => {
    if (!isOpen) setTyped('');
  }, [isOpen]);

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      variant={variant}
      size={resolvedSize}
    >
      <Modal.Header
        title={title}
        subtitle={
          subtitle ??
          (variant === 'danger'
            ? ORGANISM_PROPS.CONFIRM_ACTION_MODAL.DANGER_SUBTITLE
            : undefined)
        }
        icon={resolvedIcon}
      />

      <Modal.Body className="flex flex-col gap-4">
        {/* Description */}
        <Text as="p" size="base" color="muted" leading="relaxed">
          {description}
        </Text>

        {/* ConfigInput — only rendered when confirmWord is provided */}
        {requiresInput && (
          <div>
            <ConfigInput
              label={inputLabel}
              placeholder={placeholder ?? `Type "${confirmWord}" to confirm`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              focusRingColor={inputFocusColors[variant]}
              disabled={loading}
            />
            {hint && (
              <Text as="p" size="md" color="muted" className="mt-1">
                Enter{' '}
                <Text
                  as="code"
                  size="md"
                  color="soft"
                  className={`font-mono ${APP_BG.canvas} border border-gray-200 dark:border-gray-700 px-1 py-0.5 rounded`}
                >
                  {confirmWord}
                </Text>{' '}
                {hint}
              </Text>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Modal.CancelButton onClick={handleClose} disabled={loading}>
          {cancelLabel}
        </Modal.CancelButton>
        <Modal.ConfirmButton
          onClick={onConfirm}
          disabled={!isConfirmEnabled}
          loading={loading}
          icon={confirmIcon}
        >
          {confirmLabel}
        </Modal.ConfirmButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmActionModal;
